import { useEffect, useState, useMemo, useCallback } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import type { ShortcutConfig } from '@/hooks/use-keyboard-shortcuts'

interface KeyBadgeProps {
  children: React.ReactNode
  className?: string
}

function KeyBadge({ children, className }: KeyBadgeProps) {
  return (
    <kbd
      className={cn(
        'inline-flex h-6 min-w-[24px] items-center justify-center rounded border border-border bg-muted px-1.5 font-mono text-xs font-medium text-muted-foreground shadow-sm',
        className
      )}
    >
      {children}
    </kbd>
  )
}

function formatKeyCombo(shortcut: ShortcutConfig): React.ReactNode {
  const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.userAgent)

  // Leader sequence, e.g. "g d" \u2014 render as two separate badges ("then").
  if (shortcut.key.includes(' ')) {
    const seq = shortcut.key.split(' ').filter(Boolean)
    return (
      <span className="flex items-center gap-1">
        {seq.map((part, i) => (
          <span key={i} className="flex items-center gap-1">
            {i > 0 && <span className="text-[10px] text-muted-foreground/60">puis</span>}
            <KeyBadge>{part.toUpperCase()}</KeyBadge>
          </span>
        ))}
      </span>
    )
  }

  const parts: string[] = []

  if (shortcut.ctrlKey) parts.push(isMac ? '\u2318' : 'Ctrl')
  if (shortcut.altKey) parts.push(isMac ? '\u2325' : 'Alt')
  if (shortcut.shiftKey) parts.push(isMac ? '\u21E7' : 'Shift')

  const displayKey = shortcut.key === '/' ? '/' : (shortcut.key ?? '').toUpperCase()
  parts.push(displayKey)

  return (
    <span className="flex items-center gap-1">
      {parts.map((part, i) => (
        <KeyBadge key={i}>{part}</KeyBadge>
      ))}
    </span>
  )
}

const IGNORED_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT'])

interface ShortcutsHelpProps {
  shortcuts: ShortcutConfig[]
}

/**
 * Fire this custom event from anywhere (e.g. a TopBar "?" button) to open the
 * keyboard-shortcuts help overlay without a keypress.
 *   window.dispatchEvent(new Event('sgrc:open-shortcuts-help'))
 */
export const OPEN_SHORTCUTS_HELP_EVENT = 'sgrc:open-shortcuts-help'

export function openShortcutsHelp() {
  window.dispatchEvent(new Event(OPEN_SHORTCUTS_HELP_EVENT))
}

export function ShortcutsHelp({ shortcuts }: ShortcutsHelpProps) {
  const [open, setOpen] = useState(false)

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.target instanceof HTMLElement) {
        if (IGNORED_TAGS.has(e.target.tagName) || e.target.isContentEditable) return
      }
      // Support both "?" and Shift+/ (the physical key that produces ?).
      if ((e.key === '?' || (e.key === '/' && e.shiftKey)) && !e.ctrlKey && !e.altKey && !e.metaKey) {
        e.preventDefault()
        setOpen((prev) => !prev)
      }
    },
    []
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    const openHandler = () => setOpen(true)
    window.addEventListener(OPEN_SHORTCUTS_HELP_EVENT, openHandler)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener(OPEN_SHORTCUTS_HELP_EVENT, openHandler)
    }
  }, [handleKeyDown])

  const grouped = useMemo(() => {
    const map = new Map<string, ShortcutConfig[]>()
    for (const s of shortcuts) {
      const list = map.get(s.category) ?? []
      list.push(s)
      map.set(s.category, list)
    }
    return map
  }, [shortcuts])

  const categoryOrder = ['General', 'Navigation', 'Actions']

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Raccourcis clavier</DialogTitle>
          <DialogDescription>
            Utilisez ces raccourcis pour naviguer rapidement dans la plateforme.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-5 max-h-[60vh] overflow-y-auto pr-1">
          {categoryOrder
            .filter((cat) => grouped.has(cat))
            .map((category) => (
              <div key={category}>
                <h3 className="mb-2 text-sm font-semibold text-foreground">
                  {category}
                </h3>
                <div className="flex flex-col gap-1.5">
                  {grouped.get(category)!.map((shortcut, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between rounded-md px-2 py-1.5 hover:bg-muted/50"
                    >
                      <span className="text-sm text-muted-foreground">
                        {shortcut.description}
                      </span>
                      {formatKeyCombo(shortcut)}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          <div className="border-t pt-2">
            <div className="flex items-center justify-between rounded-md px-2 py-1.5">
              <span className="text-sm text-muted-foreground">
                Afficher / masquer cette aide
              </span>
              <KeyBadge>?</KeyBadge>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
